import React, { useState, useEffect, useCallback, useRef, Component, ErrorInfo, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { get, post } from '../../services/smartApiService';
import { OPTIONS_URLS } from '../../api/OptionsUrls';
import StrategyBuilder from './components/StrategyBuilder';
import CollapsiblePanel from './components/CollapsiblePanel';
import TechnicalIndicators from './components/TechnicalIndicators';
import PricePatternsTable from '../../Components/PricePatternsTable';
import AdvancedTradingChart from './components/AdvancedTradingChart';
import OIChart from './components/OIChart';
import OptionsChatBot from './components/OptionsChatBot';
import { kiteWebSocket, TickData, InstrumentToken } from '../../services/kiteWebSocketService';

// Error Boundary to prevent full dashboard crash
class DashboardErrorBoundary extends Component<{ children: ReactNode; name: string }, { hasError: boolean; error: string }> {
  state = { hasError: false, error: '' };
  static getDerivedStateFromError(error: Error) { return { hasError: true, error: error.message }; }
  componentDidCatch(error: Error, info: ErrorInfo) { console.error(`[${this.props.name}] Error:`, error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '1rem', backgroundColor: '#2b1111', borderRadius: '4px', margin: '0.5rem 0' }}>
          <p style={{ color: '#ef5350', fontSize: '0.75rem', fontWeight: 600 }}>{this.props.name} crashed</p>
          <p style={{ color: '#787b86', fontSize: '0.65rem' }}>{this.state.error}</p>
          <button onClick={() => this.setState({ hasError: false, error: '' })}
            style={{ marginTop: '0.5rem', padding: '0.25rem 0.5rem', fontSize: '0.65rem', backgroundColor: '#363a45', border: 'none', color: '#d1d4dc', borderRadius: '3px', cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

interface OptionChainRow {
  strike: number;
  callOI: number;
  callVolume: number;
  callLTP: number;
  callIV: string;
  callBid: number;
  callAsk: number;
  putOI: number;
  putVolume: number;
  putLTP: number;
  putIV: string;
  putBid: number;
  putAsk: number;
}

interface MarketData {
  symbol: string;
  spot_price: number;
  open: number;
  high: number;
  low: number;
  close: number;
  change: number;
  change_percent: number;
  volume: number;
}

interface NewsItem {
  headline: string;
  summary?: string;
  impact: string;
  sentiment?: string;
  source: string;
  relevance?: string;
  url?: string;
}

interface MarketNews {
  global_news: NewsItem[];
  indian_news: NewsItem[];
  overall_global_sentiment: string;
  overall_indian_sentiment: string;
  overall_sentiment?: string;
  sentiment_summary?: string;
  market_context: string;
  key_events: string[];
  fii_dii_sentiment?: string;
  fetch_timestamp: string;
  fetched_at?: string;
}

interface OIAnalysis {
  call_oi_buildup: string;
  put_oi_buildup: string;
  oi_change_signal: string;
}

interface TechnicalAnalysis {
  rsi_interpretation?: string;
  macd_interpretation?: string;
  moving_avg_interpretation?: string;
  overall_technical_signal?: string;
}

interface AIAnalysis {
  symbol: string;
  trend: string;
  sentiment: string;
  sentiment_reason?: string;
  recommended_strategy: string;
  support_levels: Array<{
    level: number;
    type: string;
    strength: string;
    reason: string;
  }>;
  resistance_levels: Array<{
    level: number;
    type: string;
    strength: string;
    reason: string;
  }>;
  pcr_interpretation: string;
  oi_analysis?: OIAnalysis;
  technical_analysis?: TechnicalAnalysis;
  key_insights: string[];
  risk_factors: string[];
  trading_signal: string;
  confidence: number;
  analysis_timestamp: string;
}

interface AnalysisHistory {
  id: number;
  symbol: string;
  expiry_date: string;
  spot_price: number;
  pcr: number;
  trend: string;
  trading_signal: string;
  confidence: number;
  analysis_timestamp: string;
  created_at: string;
}

// Check if NSE market is currently open (9:15 AM - 3:30 PM IST, Mon-Fri)
const isMarketOpen = (): boolean => {
  const now = new Date();
  const ist = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const day = ist.getDay(); // 0=Sun, 6=Sat
  if (day === 0 || day === 6) return false;
  const hours = ist.getHours();
  const mins = ist.getMinutes();
  const totalMins = hours * 60 + mins;
  return totalMins >= 555 && totalMins <= 930; // 9:15 to 15:30
};

const IndexDashboard: React.FC = () => {
  const { symbol } = useParams<{ symbol?: string }>();
  const [selectedSymbol, setSelectedSymbol] = useState(symbol?.toUpperCase() || 'NIFTY');
  const [optionChain, setOptionChain] = useState<OptionChainRow[]>([]);
  const [aiAnalysis, setAiAnalysis] = useState<AIAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [marketData, setMarketData] = useState<MarketData | null>(null);
  const [pcr, setPcr] = useState<number>(0);
  const [maxPain, setMaxPain] = useState<number>(0);
  const [selectedExpiry, setSelectedExpiry] = useState<string>('');
  const [availableExpiries, setAvailableExpiries] = useState<string[]>([]);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [countdown, setCountdown] = useState(300); // 5 minutes in seconds
  const [analysisHistory, setAnalysisHistory] = useState<AnalysisHistory[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [analysisId, setAnalysisId] = useState<number | null>(null);
  const [analysisStatus, setAnalysisStatus] = useState<string>('idle'); // idle, pending, processing, completed, failed
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);

  // News state
  const [marketNews, setMarketNews] = useState<MarketNews | null>(null);
  const [newsAnalysisId, setNewsAnalysisId] = useState<number | null>(null);
  const [newsStatus, setNewsStatus] = useState<string>('idle'); // idle, pending, processing, completed, failed
  const [newsMinimized, setNewsMinimized] = useState<boolean>(false);

  // Technical indicators state
  const [technicalIndicators, setTechnicalIndicators] = useState<any>(null);

  // OI Change tracking
  const [oiChangeData, setOiChangeData] = useState<{ strike: number; callOIChange: number; putOIChange: number }[]>([]);
  const [oiChangeTime, setOiChangeTime] = useState<Date | null>(null);

  // OI-based dynamic support/resistance levels for chart
  const [oiLevels, setOiLevels] = useState<{ level: number; type: 'oi_support' | 'oi_resistance' | 'max_pain'; label: string; oi?: number }[]>([]);

  // PCR Trend tracking
  const [pcrTrend, setPcrTrend] = useState<{ time: string; pcr: number }[]>([]);

  // Confluence alerts
  const [confluenceAlerts, setConfluenceAlerts] = useState<{ message: string; type: 'bullish' | 'bearish' | 'neutral'; strike: number }[]>([]);

  // Historical data fetch state
  const [showFetchHistoricalModal, setShowFetchHistoricalModal] = useState<boolean>(false);

  // Modal states
  const [showTechnicalIndicatorsModal, setShowTechnicalIndicatorsModal] = useState<boolean>(false);
  const [showStrategyBuilderModal, setShowStrategyBuilderModal] = useState<boolean>(false);
  const [showNewsModal, setShowNewsModal] = useState<boolean>(false);
  const [showAIInsightsModal, setShowAIInsightsModal] = useState<boolean>(false);
  const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false);
  const [showAllStrikesModal, setShowAllStrikesModal] = useState<boolean>(false);
  const [showPatternsModal, setShowPatternsModal] = useState<boolean>(false);
  const [showOptionChainPanel, setShowOptionChainPanel] = useState<boolean>(false);

  // WebSocket state
  const [wsEnabled, setWsEnabled] = useState(false);
  const [wsStatus, setWsStatus] = useState<'disconnected' | 'connecting' | 'connected' | 'error'>('disconnected');
  const [wsMessage, setWsMessage] = useState<string>('');
  const tokenMapRef = useRef<Map<number, InstrumentToken>>(new Map());
  const spotTokenRef = useRef<number | null>(null);

  // X Feed state
  interface XFeedItem {
    id: string;
    text: string;
    timestamp: string;
    sentiment?: string;
    url?: string;
  }
  const [xFeedItems, setXFeedItems] = useState<XFeedItem[]>([]);
  const [xFeedLoading, setXFeedLoading] = useState(false);
  const [lastXFeedRefresh, setLastXFeedRefresh] = useState<Date | null>(null);
  const [xFeedCountdown, setXFeedCountdown] = useState(1800); // 30 minutes in seconds

  // Live price update state
  const last15minUpdateRef = useRef<number>(0); // Timestamp of last 15min update
  const currentCandleDataRef = useRef<any>({
    open: null,
    high: null,
    low: null,
    close: null,
    volume: 0,
    timestamp: null
  });

  // Compute max pain client-side from option chain (avoids separate API call)
  const computeMaxPain = (chain: OptionChainRow[]): number => {
    if (chain.length === 0) return 0;
    let minPain = Infinity, maxPainStrike = 0;
    for (const target of chain) {
      let totalPain = 0;
      for (const row of chain) {
        if (target.strike > row.strike) totalPain += row.callOI * (target.strike - row.strike);
        if (target.strike < row.strike) totalPain += row.putOI * (row.strike - target.strike);
      }
      if (totalPain < minPain) { minPain = totalPain; maxPainStrike = target.strike; }
    }
    return maxPainStrike;
  };

  // Load option chain data — combined into fewer API calls
  const loadOptionChain = async () => {
    setLoading(true);

    try {
      // Single call: chain returns spot_price, pcr, option_chain, available_expiries
      const chainResponse: any = await get(
        OPTIONS_URLS.OPTION_CHAIN(selectedSymbol, selectedExpiry),
        { showLoading: false }
      ).toPromise();

      if (chainResponse?.data) {
        const chain = chainResponse.data.option_chain || [];
        setOptionChain(chain);
        setPcr(chainResponse.data.pcr || 0);
        setAvailableExpiries(chainResponse.data.available_expiries || []);

        // Use spot_price from chain response (saves a separate /spot/ call)
        if (chainResponse.data.spot_price) {
          setMarketData(prev => prev ? { ...prev, spot_price: chainResponse.data.spot_price } : {
            symbol: selectedSymbol,
            spot_price: chainResponse.data.spot_price,
            open: 0, high: 0, low: 0, close: 0, change: 0, change_percent: 0, volume: 0,
          });
        }

        if (!selectedExpiry && chainResponse.data.expiry_date) {
          setSelectedExpiry(chainResponse.data.expiry_date);
        }

        // Compute max pain client-side (saves a separate /max-pain/ call)
        setMaxPain(computeMaxPain(chain));
      }

      // Fetch full spot data only on first load (for open/high/low/close/change)
      if (!marketData) {
        try {
          const spotResponse: any = await get(OPTIONS_URLS.SPOT_PRICE(selectedSymbol), { showLoading: false }).toPromise();
          if (spotResponse?.data) setMarketData(spotResponse.data);
        } catch (e) { /* chain already provided spot_price */ }
      }

      // Fetch OI change from backend (if snapshots exist)
      try {
        const oiChangeResponse: any = await get(
          OPTIONS_URLS.OI_CHANGE(selectedSymbol, selectedExpiry),
          { showLoading: false }
        ).toPromise();

        if (oiChangeResponse?.data?.strikes?.length > 0) {
          setOiChangeData(oiChangeResponse.data.strikes);
          setOiChangeTime(oiChangeResponse.data.current_time ? new Date(oiChangeResponse.data.current_time) : null);
        }
      } catch (e) {
        // Silently fail — OI change is supplementary
      }

    } catch (error) {
      console.error('Error loading option chain:', error);
    } finally {
      setLoading(false);
    }
  };

  // Compute dynamic OI-based S/R levels — THROTTLED (every 60s, not every tick)
  // OI levels use refs to avoid re-running on every WebSocket tick
  const optionChainForOIRef = useRef(optionChain);
  const marketDataForOIRef = useRef(marketData);
  const oiChangeForOIRef = useRef(oiChangeData);
  optionChainForOIRef.current = optionChain;
  marketDataForOIRef.current = marketData;
  oiChangeForOIRef.current = oiChangeData;

  const computeOILevels = useCallback(() => {
    const chain = optionChainForOIRef.current;
    const mktData = marketDataForOIRef.current;
    const oiChgData = oiChangeForOIRef.current;
    if (chain.length === 0 || !mktData) return;

    const levels: typeof oiLevels = [];
    let maxCallOI = 0, maxCallOIStrike = 0;
    let maxPutOI = 0, maxPutOIStrike = 0;

    chain.forEach(row => {
      if (row.strike > mktData.spot_price && row.callOI > maxCallOI) { maxCallOI = row.callOI; maxCallOIStrike = row.strike; }
      if (row.strike < mktData.spot_price && row.putOI > maxPutOI) { maxPutOI = row.putOI; maxPutOIStrike = row.strike; }
    });

    let secondCallOI = 0, secondCallStrike = 0, secondPutOI = 0, secondPutStrike = 0;
    chain.forEach(row => {
      if (row.strike > mktData.spot_price && row.callOI > secondCallOI && row.strike !== maxCallOIStrike) { secondCallOI = row.callOI; secondCallStrike = row.strike; }
      if (row.strike < mktData.spot_price && row.putOI > secondPutOI && row.strike !== maxPutOIStrike) { secondPutOI = row.putOI; secondPutStrike = row.strike; }
    });

    if (maxPutOIStrike > 0) levels.push({ level: maxPutOIStrike, type: 'oi_support', label: 'OI Support', oi: maxPutOI });
    if (secondPutStrike > 0) levels.push({ level: secondPutStrike, type: 'oi_support', label: 'OI Support 2', oi: secondPutOI });
    if (maxCallOIStrike > 0) levels.push({ level: maxCallOIStrike, type: 'oi_resistance', label: 'OI Resistance', oi: maxCallOI });
    if (secondCallStrike > 0) levels.push({ level: secondCallStrike, type: 'oi_resistance', label: 'OI Resist 2', oi: secondCallOI });
    if (maxPain > 0) levels.push({ level: maxPain, type: 'max_pain', label: 'Max Pain' });

    // Only update state if levels actually changed (prevent chart line flicker)
    setOiLevels(prev => {
      const prevKey = prev.map(l => `${l.type}:${l.level}`).join(',');
      const newKey = levels.map(l => `${l.type}:${l.level}`).join(',');
      return prevKey === newKey ? prev : levels;
    });

    // Confluence alerts
    const alerts: typeof confluenceAlerts = [];
    const spotPrice = mktData.spot_price;
    const stepSize = chain[1]?.strike - chain[0]?.strike || 50;
    const proximityThreshold = stepSize * 2;

    if (maxCallOIStrike > 0 && Math.abs(spotPrice - maxCallOIStrike) <= proximityThreshold) {
      const oiChg = oiChgData.find(d => d.strike === maxCallOIStrike);
      if (oiChg && oiChg.callOIChange > 0) {
        alerts.push({ message: `Approaching resistance ${maxCallOIStrike} with Call OI building (+${(oiChg.callOIChange / 1000).toFixed(0)}K) — strong resistance`, type: 'bearish', strike: maxCallOIStrike });
      } else if (oiChg && oiChg.callOIChange < 0) {
        alerts.push({ message: `Near resistance ${maxCallOIStrike} but Call OI unwinding (${(oiChg.callOIChange / 1000).toFixed(0)}K) — potential breakout`, type: 'bullish', strike: maxCallOIStrike });
      } else {
        alerts.push({ message: `Price near resistance ${maxCallOIStrike} (highest Call OI: ${(maxCallOI / 100000).toFixed(1)}L)`, type: 'neutral', strike: maxCallOIStrike });
      }
    }

    if (maxPutOIStrike > 0 && Math.abs(spotPrice - maxPutOIStrike) <= proximityThreshold) {
      const oiChg = oiChgData.find(d => d.strike === maxPutOIStrike);
      if (oiChg && oiChg.putOIChange > 0) {
        alerts.push({ message: `Approaching support ${maxPutOIStrike} with Put OI building (+${(oiChg.putOIChange / 1000).toFixed(0)}K) — strong support`, type: 'bullish', strike: maxPutOIStrike });
      } else if (oiChg && oiChg.putOIChange < 0) {
        alerts.push({ message: `Near support ${maxPutOIStrike} but Put OI unwinding (${(oiChg.putOIChange / 1000).toFixed(0)}K) — potential breakdown`, type: 'bearish', strike: maxPutOIStrike });
      } else {
        alerts.push({ message: `Price near support ${maxPutOIStrike} (highest Put OI: ${(maxPutOI / 100000).toFixed(1)}L)`, type: 'neutral', strike: maxPutOIStrike });
      }
    }

    setConfluenceAlerts(alerts);
  }, [maxPain]); // Only re-create when maxPain changes; reads data from refs

  // Run OI level computation on initial load + every 60 seconds (NOT on every tick)
  useEffect(() => {
    computeOILevels(); // Immediate first run
    const interval = setInterval(computeOILevels, 60 * 1000);
    return () => clearInterval(interval);
  }, [computeOILevels]);

  // Track PCR trend — only when PCR meaningfully changes
  const lastPcrRef = useRef<number>(0);
  useEffect(() => {
    if (pcr > 0 && Math.abs(pcr - lastPcrRef.current) > 0.005) {
      lastPcrRef.current = pcr;
      setPcrTrend(prev => {
        const entry = { time: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }), pcr };
        return [...prev, entry].slice(-30);
      });
    }
  }, [pcr]);

  // AI Analysis using Grok (Async with polling)
  const getAIAnalysis = async () => {
    if (!marketData || optionChain.length === 0) {
      alert('Please load option chain data first');
      return;
    }

    // Check if analysis is already in progress
    if (analysisStatus === 'pending' || analysisStatus === 'processing') {
      console.log('Analysis already in progress, skipping...');
      return;
    }

    setAnalysisStatus('pending');

    try {
      // Merge OI change data into option chain for AI analysis
      const oiChangeMap = new Map(oiChangeData.map(d => [d.strike, d]));
      const enrichedChain = optionChain.map(row => ({
        ...row,
        callOIChange: oiChangeMap.get(row.strike)?.callOIChange || 0,
        putOIChange: oiChangeMap.get(row.strike)?.putOIChange || 0,
      }));

      const payload = {
        symbol: selectedSymbol,
        spot_price: marketData.spot_price,
        option_chain: enrichedChain,
        pcr: pcr,
        expiry_date: selectedExpiry,
        analysis_type: 'comprehensive',
        technical_indicators: technicalIndicators
      };

      // Initiate async analysis
      const response: any = await post(OPTIONS_URLS.ANALYZE_ASYNC, payload, { showLoading: false }).toPromise();

      console.log('Async analysis response:', response);

      // Response comes from axios: response.data contains the backend response
      const backendData = response.data;
      console.log('Backend data:', backendData);

      if (backendData && backendData.analysis_id) {
        setAnalysisId(backendData.analysis_id);
        setAnalysisStatus(backendData.status || 'pending');
        console.log(`Analysis initiated with ID: ${backendData.analysis_id}, Status: ${backendData.status || 'pending'}`);
        // Polling will start automatically via useEffect
      } else {
        console.error('No analysis ID in backend response:', backendData);
        setAnalysisStatus('failed');
      }
    } catch (error) {
      console.error('Error initiating AI analysis:', error);
      alert('Failed to initiate AI analysis. Please try again.');
      setAnalysisStatus('failed');
    }
  };

  // Poll for analysis status
  const pollAnalysisStatus = async (id: number) => {
    try {
      console.log(`Polling analysis status for ID: ${id}`);
      const response: any = await get(OPTIONS_URLS.ANALYSIS_STATUS(id), { showLoading: false }).toPromise();

      console.log('Poll response:', response);

      // Response comes from axios: response.data contains the backend response
      const data = response.data;
      console.log('Poll backend data:', data);

      if (data && data.id) {
        const status = data.status;
        setAnalysisStatus(status);
        console.log(`Analysis ID ${id} status: ${status}`);

        if (status === 'completed') {
          // Analysis is complete, set the data
          console.log('Analysis completed, setting AI analysis data');
          setAiAnalysis({
            symbol: data.symbol,
            trend: data.trend,
            sentiment: data.sentiment,
            sentiment_reason: data.sentiment_reason,
            recommended_strategy: data.recommended_strategy,
            support_levels: data.support_levels || [],
            resistance_levels: data.resistance_levels || [],
            pcr_interpretation: data.pcr_interpretation,
            oi_analysis: data.oi_analysis,
            technical_analysis: data.technical_analysis,
            key_insights: data.key_insights || [],
            risk_factors: data.risk_factors || [],
            trading_signal: data.trading_signal,
            confidence: data.confidence,
            analysis_timestamp: data.analysis_timestamp
          });
          setAnalysisId(null);
          setAnalysisStatus('completed');
          loadAnalysisHistory(); // Refresh history
        } else if (status === 'failed') {
          console.error('Analysis failed');
          alert('Analysis failed. Please try again.');
          setAnalysisId(null);
          setAnalysisStatus('failed');
        }
      } else {
        console.error('No data in poll response or missing ID');
      }
    } catch (error) {
      console.error('Error polling analysis status:', error);
    }
  };

  // Load analysis history
  const loadAnalysisHistory = async () => {
    try {
      const response: any = await get(
        OPTIONS_URLS.ANALYSIS_HISTORY(selectedSymbol, selectedExpiry, 10),
        { showLoading: false }
      ).toPromise();

      if (response?.data) {
        setAnalysisHistory(response.data);
      }
    } catch (error) {
      console.error('Error loading analysis history:', error);
    }
  };

  // View specific analysis from history
  const viewHistoricalAnalysis = async (id: number) => {
    try {
      const history = analysisHistory.find(h => h.id === id);
      if (history) {
        // Convert history record to AIAnalysis format
        const analysis: AIAnalysis = {
          symbol: history.symbol,
          trend: history.trend,
          sentiment: 'neutral',
          recommended_strategy: '',
          support_levels: [],
          resistance_levels: [],
          pcr_interpretation: '',
          key_insights: [],
          risk_factors: [],
          trading_signal: history.trading_signal,
          confidence: history.confidence,
          analysis_timestamp: history.analysis_timestamp
        };
        setAiAnalysis(analysis);
        alert(`Viewing analysis from ${new Date(history.created_at).toLocaleString()}`);
      }
    } catch (error) {
      console.error('Error viewing analysis:', error);
    }
  };

  // Auto refresh — only reloads data, does NOT auto-trigger AI (costs tokens)
  const refreshAll = async () => {
    await loadOptionChain();
  };

  // Load PCR trend from backend snapshots
  const loadPcrTrend = async () => {
    try {
      const response: any = await get(OPTIONS_URLS.PCR_TREND(selectedSymbol, 8), { showLoading: false }).toPromise();
      if (response?.data?.pcr_trend?.length > 0) {
        setPcrTrend(response.data.pcr_trend.map((pt: any) => ({
          time: new Date(pt.time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
          pcr: pt.pcr,
        })));
      }
    } catch (e) {
      // Silently fail
    }
  };

  // Initial load
  useEffect(() => {
    loadOptionChain();
    loadAnalysisHistory();
    loadPcrTrend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSymbol, selectedExpiry]);

  // Polling effect for analysis status
  useEffect(() => {
    if (!analysisId || analysisStatus === 'completed' || analysisStatus === 'failed') {
      return;
    }

    // Poll every 15 seconds
    const pollInterval = setInterval(() => {
      pollAnalysisStatus(analysisId);
    }, 15000);

    // Initial poll immediately
    pollAnalysisStatus(analysisId);

    return () => clearInterval(pollInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisId, analysisStatus]);

  // Auto refresh timer (disabled when WebSocket is active)
  useEffect(() => {
    if (!autoRefresh || wsEnabled) {
      setCountdown(300);
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          refreshAll();
          return 300;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, marketData, optionChain, wsEnabled]);

  // Function to send live price update to backend (only during market hours)
  const sendLivePriceUpdate = useCallback(async () => {
    if (!marketData || !wsEnabled || !isMarketOpen()) return;

    try {
      const candleData = currentCandleDataRef.current;

      // Use candle OHLC only from accumulated tick data, NOT day OHLC as fallback
      const candleOpen = candleData.open ?? marketData.spot_price;
      const candleHigh = candleData.high ?? marketData.spot_price;
      const candleLow = candleData.low ?? marketData.spot_price;
      const candleClose = candleData.close ?? marketData.spot_price;

      const payload = {
        symbol: selectedSymbol,
        current_price: marketData.spot_price,
        day_high: marketData.high,
        day_low: marketData.low,
        day_open: marketData.open,
        previous_close: marketData.close,
        volume: marketData.volume,
        change: marketData.change,
        change_percent: marketData.change_percent,
        open: candleOpen,
        high: candleHigh,
        low: candleLow,
        close: candleClose,
        timestamp: new Date().toISOString(),
        create_candle: true
      };

      console.log('Sending live price update:', payload);

      post(OPTIONS_URLS.LIVE_PRICE_UPDATE, payload, {
        showLoading: false,
        requiresAuthorization: false
      }).subscribe({
        next: (response: any) => {
          console.log('Live price update success:', response);
          // Reset candle data for next interval
          currentCandleDataRef.current = {
            open: null,
            high: null,
            low: null,
            close: null,
            volume: 0,
            timestamp: null
          };
        },
        error: (err: any) => {
          console.error('Live price update error:', err);
        }
      });
    } catch (error) {
      console.error('Error sending live price update:', error);
    }
  }, [marketData, selectedSymbol, wsEnabled]);

  // Batched option chain updates — collect tick changes in a map, flush via rAF
  const pendingOCUpdatesRef = useRef<Map<string, { strike: number; type: 'CE' | 'PE'; tick: TickData }>>(new Map());
  const ocFlushScheduledRef = useRef(false);

  const flushOptionChainUpdates = useCallback(() => {
    ocFlushScheduledRef.current = false;
    const updates = pendingOCUpdatesRef.current;
    if (updates.size === 0) return;

    setOptionChain(prev => {
      const result = [...prev];
      for (let i = 0; i < result.length; i++) {
        const ceKey = `${result[i].strike}:CE`;
        const peKey = `${result[i].strike}:PE`;
        const ceUpdate = updates.get(ceKey);
        const peUpdate = updates.get(peKey);

        if (ceUpdate) {
          result[i] = {
            ...result[i],
            callLTP: ceUpdate.tick.lastPrice,
            callVolume: ceUpdate.tick.volume ?? result[i].callVolume,
            callOI: ceUpdate.tick.oi ?? result[i].callOI,
            callBid: ceUpdate.tick.bid?.[0]?.price ?? result[i].callBid,
            callAsk: ceUpdate.tick.ask?.[0]?.price ?? result[i].callAsk,
          };
        }
        if (peUpdate) {
          result[i] = {
            ...result[i],
            putLTP: peUpdate.tick.lastPrice,
            putVolume: peUpdate.tick.volume ?? result[i].putVolume,
            putOI: peUpdate.tick.oi ?? result[i].putOI,
            putBid: peUpdate.tick.bid?.[0]?.price ?? result[i].putBid,
            putAsk: peUpdate.tick.ask?.[0]?.price ?? result[i].putAsk,
          };
        }
      }
      return result;
    });

    updates.clear();
  }, []);

  // WebSocket handlers
  const handleWebSocketTick = useCallback((ticks: Map<number, TickData>) => {
    ticks.forEach((tick, token) => {
      const instrument = tokenMapRef.current.get(token);
      if (!instrument) return;

      if (instrument.type === 'INDEX' && spotTokenRef.current === token) {
        const currentPrice = tick.lastPrice;
        const candleData = currentCandleDataRef.current;

        if (candleData.open === null) {
          candleData.open = currentPrice;
          candleData.timestamp = new Date();
        }
        if (candleData.high === null || currentPrice > candleData.high) candleData.high = currentPrice;
        if (candleData.low === null || currentPrice < candleData.low) candleData.low = currentPrice;
        candleData.close = currentPrice;
        candleData.volume = tick.volume || 0;

        setMarketData(prev => prev ? {
          ...prev,
          spot_price: tick.lastPrice,
          high: (tick.high && tick.high > 0) ? tick.high : prev.high,
          low: (tick.low && tick.low > 0) ? tick.low : prev.low,
          open: (tick.open && tick.open > 0) ? tick.open : prev.open,
          close: (tick.close && tick.close > 0) ? tick.close : prev.close,
          change: tick.change ?? prev.change,
          change_percent: tick.changePercent ?? prev.change_percent,
          volume: tick.volume ?? prev.volume
        } : null);
      } else if (instrument.type === 'CE' || instrument.type === 'PE') {
        // Batch option chain updates — collect in map, flush once per animation frame
        const strike = instrument.strike ?? 0;
        const key = `${strike}:${instrument.type}`;
        pendingOCUpdatesRef.current.set(key, { strike, type: instrument.type as 'CE' | 'PE', tick });

        if (!ocFlushScheduledRef.current) {
          ocFlushScheduledRef.current = true;
          requestAnimationFrame(flushOptionChainUpdates);
        }
      }
    });
  }, [flushOptionChainUpdates]);

  const startWebSocket = async () => {
    try {
      setWsStatus('connecting');
      setWsMessage('Fetching credentials...');

      // Get WebSocket credentials
      const credResponse: any = await get(OPTIONS_URLS.WEBSOCKET_CREDENTIALS, { showLoading: false }).toPromise();
      if (!credResponse?.data) {
        throw new Error('Failed to get WebSocket credentials');
      }

      const { api_key, access_token } = credResponse.data;

      // Get instrument tokens
      setWsMessage('Fetching instrument tokens...');
      const tokensResponse: any = await get(OPTIONS_URLS.INSTRUMENT_TOKENS(selectedSymbol), { showLoading: false }).toPromise();
      if (!tokensResponse?.data?.tokens) {
        throw new Error('Failed to get instrument tokens');
      }

      const instruments: InstrumentToken[] = tokensResponse.data.tokens;

      // Build token map
      const tokenMap = new Map<number, InstrumentToken>();
      const tokens: number[] = [];

      instruments.forEach(inst => {
        tokenMap.set(inst.token, inst);
        tokens.push(inst.token);
        if (inst.type === 'INDEX') {
          spotTokenRef.current = inst.token;
        }
      });

      tokenMapRef.current = tokenMap;

      // Initialize and connect WebSocket
      setWsMessage('Connecting to Kite...');
      kiteWebSocket.initialize(api_key, access_token);
      kiteWebSocket.setTokenMap(instruments);

      await kiteWebSocket.connect();

      // Subscribe to tokens
      // Use 'full' mode to get OI updates in real-time (quote mode doesn't include OI)
      kiteWebSocket.subscribe(tokens, 'full');

      setWsEnabled(true);
      setWsMessage('Live data streaming');

    } catch (error: any) {
      console.error('WebSocket connection error:', error);
      setWsStatus('error');
      setWsMessage(error.message || 'Connection failed');
      alert(`Failed to start WebSocket: ${error.message || 'Unknown error'}`);
    }
  };

  const stopWebSocket = () => {
    kiteWebSocket.disconnect();
    setWsEnabled(false);
    setWsStatus('disconnected');
    setWsMessage('');
    spotTokenRef.current = null;
    tokenMapRef.current.clear();
  };

  // WebSocket effects
  useEffect(() => {
    const unsubscribeTick = kiteWebSocket.onTick(handleWebSocketTick);
    const unsubscribeStatus = kiteWebSocket.onStatus((status, message) => {
      setWsStatus(status);
      if (message) setWsMessage(message);
    });

    return () => {
      unsubscribeTick();
      unsubscribeStatus();
    };
  }, [handleWebSocketTick]);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (wsEnabled) {
        kiteWebSocket.disconnect();
      }
    };
  }, [wsEnabled]);

  // 15-minute interval for sending live price updates
  useEffect(() => {
    if (!wsEnabled) {
      last15minUpdateRef.current = 0;
      return;
    }

    // Function to check if we need to send update
    const checkAndSendUpdate = () => {
      const now = Date.now();
      const elapsed = now - last15minUpdateRef.current;
      const fifteenMinutes = 15 * 60 * 1000; // 15 minutes in milliseconds

      if (elapsed >= fifteenMinutes || last15minUpdateRef.current === 0) {
        sendLivePriceUpdate();
        last15minUpdateRef.current = now;
      }
    };

    // Check immediately when WebSocket starts
    checkAndSendUpdate();

    // Check every minute (more frequent checks to ensure we don't miss the 15min mark)
    const interval = setInterval(() => {
      checkAndSendUpdate();
    }, 60 * 1000); // Check every 1 minute

    return () => clearInterval(interval);
  }, [wsEnabled, sendLivePriceUpdate]);

  // OI Snapshot: Save to backend every 5 minutes during live mode (NIFTY & BANKNIFTY only)
  // Use refs to avoid re-running effect on every tick update
  const lastOISnapshotRef = useRef<number>(0);
  const optionChainRef = useRef(optionChain);
  const marketDataRef = useRef(marketData);
  const selectedExpiryRef = useRef(selectedExpiry);
  optionChainRef.current = optionChain;
  marketDataRef.current = marketData;
  selectedExpiryRef.current = selectedExpiry;

  useEffect(() => {
    if (!wsEnabled) return;
    if (selectedSymbol !== 'NIFTY' && selectedSymbol !== 'BANKNIFTY') return;

    const saveOISnapshot = () => {
      const chain = optionChainRef.current;
      const mktData = marketDataRef.current;
      if (chain.length === 0 || !mktData || !isMarketOpen()) return;

      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;

      if (now - lastOISnapshotRef.current >= fiveMinutes || lastOISnapshotRef.current === 0) {
        lastOISnapshotRef.current = now;

        const payload = {
          symbol: selectedSymbol,
          expiry_date: selectedExpiryRef.current,
          spot_price: mktData.spot_price,
          option_chain: chain.map(row => ({
            strike: row.strike,
            callOI: row.callOI,
            putOI: row.putOI,
            callVolume: row.callVolume,
            putVolume: row.putVolume,
            callLTP: row.callLTP,
            putLTP: row.putLTP,
          })),
        };

        post(OPTIONS_URLS.OI_SNAPSHOT_SAVE, payload, { showLoading: false }).subscribe({
          next: (res: any) => {
            console.log('OI snapshot saved:', res?.data?.message);
            // Re-fetch OI change + PCR trend after save so "Change" view updates
            get(OPTIONS_URLS.OI_CHANGE(selectedSymbol, selectedExpiryRef.current), { showLoading: false }).subscribe({
              next: (changeRes: any) => {
                if (changeRes?.data?.strikes?.length > 0) {
                  setOiChangeData(changeRes.data.strikes);
                  setOiChangeTime(changeRes.data.current_time ? new Date(changeRes.data.current_time) : null);
                }
              },
            });
            get(OPTIONS_URLS.PCR_TREND(selectedSymbol), { showLoading: false }).subscribe({
              next: (pcrRes: any) => {
                if (pcrRes?.data?.length > 0) setPcrTrend(pcrRes.data);
              },
            });
          },
          error: (err: any) => console.error('OI snapshot save error:', err),
        });
      }
    };

    // Save immediately on first enable
    saveOISnapshot();

    // Check every minute (ref-based, so no effect re-runs on tick)
    const interval = setInterval(saveOISnapshot, 60 * 1000);
    return () => clearInterval(interval);
  }, [wsEnabled, selectedSymbol]);

  // Format countdown timer
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Auto-scroll to ATM strike when modal opens
  useEffect(() => {
    if (showAllStrikesModal && optionChain.length > 0 && marketData) {
      // Small delay to ensure modal DOM is rendered
      setTimeout(() => {
        const atmElement = document.getElementById('atm-strike-modal');
        if (atmElement) {
          atmElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }
  }, [showAllStrikesModal, optionChain, marketData]);

  // Calculate OI change for a strike — uses backend oiChangeData
  const calculateOIChange = (strike: number) => {
    const change = oiChangeData.find(d => d.strike === strike);
    if (!change) return { callChange: 0, putChange: 0 };
    return { callChange: change.callOIChange, putChange: change.putOIChange };
  };

  // Fetch market news using async/poll pattern
  const fetchMarketNews = async () => {
    setNewsStatus('pending');

    try {
      // Start async news fetch
      const response: any = await post(
        OPTIONS_URLS.NEWS_ASYNC(selectedSymbol),
        {},
        { showLoading: false }
      ).toPromise();

      if (response?.data?.analysis_id) {
        const newNewsAnalysisId = response.data.analysis_id;
        setNewsAnalysisId(newNewsAnalysisId);
        setNewsStatus('pending');
        console.log('News analysis initiated:', newNewsAnalysisId);

        // Start polling
        pollNewsStatus(newNewsAnalysisId);
      } else {
        throw new Error('Failed to initiate news analysis');
      }
    } catch (error) {
      console.error('Error fetching news:', error);
      setNewsStatus('failed');
      alert('Failed to fetch market news');
    }
  };

  // Poll news analysis status
  const pollNewsStatus = async (id: number) => {
    const maxAttempts = 24; // Max 24 attempts * 5 seconds = 2 minutes
    let attempts = 0;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        console.error('News polling timed out');
        setNewsStatus('failed');
          return;
      }

      try {
        const response: any = await get(
          OPTIONS_URLS.NEWS_STATUS(id),
          { showLoading: false }
        ).toPromise();

        const data = response.data;
        console.log('Poll news status:', data);

        if (data && data.id) {
          const status = data.status;
          setNewsStatus(status);
          console.log(`News analysis ${id} status: ${status}`);

          if (status === 'completed') {
            // News fetch complete, set the data
            console.log('News analysis completed, setting news data');
            const newsData: MarketNews = {
              global_news: data.global_news || [],
              indian_news: data.indian_news || [],
              overall_global_sentiment: data.overall_global_sentiment || 'neutral',
              overall_indian_sentiment: data.overall_indian_sentiment || 'neutral',
              overall_sentiment: data.overall_sentiment || 'neutral',
              sentiment_summary: data.sentiment_summary || data.market_context || '',
              market_context: data.market_context || '',
              key_events: data.key_events || [],
              fii_dii_sentiment: data.fii_dii_sentiment || '',
              fetch_timestamp: data.fetch_timestamp || data.created_at || '',
              fetched_at: data.completed_at || data.created_at || ''
            };
            // Add sentiment field to news items if not present (use impact as fallback)
            if (newsData.global_news) {
              newsData.global_news = newsData.global_news.map((item: NewsItem) => ({
                ...item,
                sentiment: item.sentiment || item.impact
              }));
            }
            if (newsData.indian_news) {
              newsData.indian_news = newsData.indian_news.map((item: NewsItem) => ({
                ...item,
                sentiment: item.sentiment || item.impact
              }));
            }
            setMarketNews(newsData);
            setNewsAnalysisId(null);
                } else if (status === 'failed') {
            console.error('News analysis failed');
            alert('News analysis failed. Please try again.');
            setNewsAnalysisId(null);
            setNewsStatus('failed');
                } else {
            // Still pending/processing, poll again after 5 seconds
            attempts++;
            setTimeout(poll, 5000);
          }
        } else {
          console.error('No data in news poll response or missing ID');
          attempts++;
          setTimeout(poll, 5000);
        }
      } catch (error) {
        console.error('Error polling news status:', error);
        attempts++;
        setTimeout(poll, 5000);
      }
    };

    poll();
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'bullish': return 'is-success';
      case 'bearish': return 'is-danger';
      default: return 'is-warning';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'bullish': return '📈';
      case 'bearish': return '📉';
      default: return '➡️';
    }
  };

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment?.toLowerCase()) {
      case 'positive': return 'is-success';
      case 'negative': return 'is-danger';
      default: return 'is-warning';
    }
  };

  const getSentimentIcon = (sentiment: string) => {
    switch (sentiment?.toLowerCase()) {
      case 'positive': return '😊';
      case 'negative': return '😟';
      default: return '😐';
    }
  };

  // ===== COMPONENT FUNCTIONS =====

  // Market Overview Cards Component
  const renderMarketOverviewCards = () => (
    <div className="columns is-multiline mb-2" style={{ margin: '0 -0.25rem' }}>
      <div className="column is-3 pb-2" style={{ padding: '0 0.25rem' }}>
        <div className="box has-background-info-light py-2 px-3 mb-0">
          <p className="heading is-size-7 mb-1">Spot Price</p>
          <p className="title is-6 mb-1">{marketData?.spot_price.toFixed(2) || '---'}</p>
          <p className={`is-size-7 ${marketData && marketData.change >= 0 ? 'has-text-success' : 'has-text-danger'}`}>
            {marketData ? `${marketData.change >= 0 ? '+' : ''}${marketData.change.toFixed(2)}` : '---'}
          </p>
        </div>
      </div>

      <div className="column is-3 pb-2" style={{ padding: '0 0.25rem' }}>
        <div className="box has-background-success-light py-2 px-3 mb-0">
          <p className="heading is-size-7 mb-1">PCR Ratio</p>
          <p className="title is-6 mb-1">{pcr.toFixed(2)}</p>
          <p className="is-size-7">{pcr > 1.2 ? 'Bullish' : pcr < 0.8 ? 'Bearish' : 'Neutral'}</p>
        </div>
      </div>

      <div className="column is-3 pb-2" style={{ padding: '0 0.25rem' }}>
        <div className="box has-background-warning-light py-2 px-3 mb-0">
          <p className="heading is-size-7 mb-1">Max Pain</p>
          <p className="title is-6 mb-1">{maxPain || '---'}</p>
          <p className="is-size-7">
            {marketData && maxPain ? `${maxPain > marketData.spot_price ? '+' : ''}${(maxPain - marketData.spot_price).toFixed(0)}` : '---'}
          </p>
        </div>
      </div>

      <div className="column is-3 pb-2" style={{ padding: '0 0.25rem' }}>
        <div className="box has-background-primary-light py-2 px-3 mb-0">
          <p className="heading is-size-7 mb-1">Expiry</p>
          <div className="select is-small is-fullwidth">
            <select value={selectedExpiry} onChange={(e) => setSelectedExpiry(e.target.value)}>
              {availableExpiries.map(exp => (
                <option key={exp} value={exp}>{exp}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  // Price Patterns Component
  const renderPricePatterns = () => (
    <PricePatternsTable
      symbol={selectedSymbol}
      limit={15}
      autoRefresh={false}
      showCompanyColumn={false}
      showTimeframeFilter={true}
      title={`${selectedSymbol} Detected Patterns`}
    />
  );

  // Quick Action Buttons Component - Vertical Layout
  const renderQuickActionButtons = () => (
    <div className="box py-2 px-3 mb-3">
      <div className="buttons">
        <button
          className="button is-small is-primary is-outlined"
          onClick={() => setShowAllStrikesModal(true)}
        >
          <span className="icon is-small">
            <i className="fa fa-table"></i>
          </span>
          <span className="is-size-7">Option Chain</span>
        </button>

        <button
          className="button is-small is-info is-outlined"
          onClick={() => setShowPatternsModal(true)}
        >
          <span className="icon is-small">
            <i className="fa fa-chart-bar"></i>
          </span>
          <span className="is-size-7">Patterns</span>
        </button>

        <button
          className={`button is-small ${
            technicalIndicators?.overall_technical_signal === 'bullish' || technicalIndicators?.overall_technical_signal === 'buy' ? 'is-success' :
            technicalIndicators?.overall_technical_signal === 'bearish' || technicalIndicators?.overall_technical_signal === 'sell' ? 'is-danger' :
            technicalIndicators?.overall_technical_signal === 'neutral' || technicalIndicators?.overall_technical_signal === 'hold' ? 'is-warning' :
            'is-light'
          }`}
          onClick={() => setShowTechnicalIndicatorsModal(true)}
        >
          <span className="icon is-small">
            <i className="fa fa-line-chart"></i>
          </span>
          <span className="is-size-7">
            {technicalIndicators?.overall_technical_signal ?
              (technicalIndicators.overall_technical_signal === 'bullish' || technicalIndicators.overall_technical_signal === 'buy' ? '📈 Buy' :
               technicalIndicators.overall_technical_signal === 'bearish' || technicalIndicators.overall_technical_signal === 'sell' ? '📉 Sell' : '➡️ Hold') :
              'Technical'}
          </span>
        </button>

        <button
          className={`button is-small ${
            marketNews?.overall_global_sentiment === 'positive' || marketNews?.overall_indian_sentiment === 'positive' ? 'is-success' :
            marketNews?.overall_global_sentiment === 'negative' || marketNews?.overall_indian_sentiment === 'negative' ? 'is-danger' :
            'is-light'
          }`}
          onClick={() => setShowNewsModal(true)}
        >
          <span className="icon is-small">
            <i className="fa fa-newspaper-o"></i>
          </span>
          <span className="is-size-7">
            {marketNews ? `📰 ${marketNews.overall_global_sentiment || 'News'}` : 'News'}
          </span>
        </button>

        <button
          className={`button is-small ${
            aiAnalysis?.trading_signal === 'BUY' ? 'is-success' :
            aiAnalysis?.trading_signal === 'SELL' ? 'is-danger' :
            aiAnalysis?.trading_signal === 'HOLD' ? 'is-warning' :
            'is-light'
          }`}
          onClick={() => setShowAIInsightsModal(true)}
        >
          <span className="icon is-small">
            <i className="fa fa-brain"></i>
          </span>
          <span className="is-size-7">
            {aiAnalysis ? `🤖 ${aiAnalysis.trading_signal}` : 'AI Insights'}
          </span>
        </button>

        <button
          className="button is-small is-info is-outlined"
          onClick={() => setShowHistoryModal(true)}
        >
          <span className="icon is-small">
            <i className="fa fa-history"></i>
          </span>
          <span className="is-size-7">
            History {analysisHistory.length > 0 && `(${analysisHistory.length})`}
          </span>
        </button>
      </div>
    </div>
  );

  // X Feed refresh function
  const refreshXFeed = async () => {
    setXFeedLoading(true);
    try {
      const response: any = await post(
        OPTIONS_URLS.X_FEED_ASYNC,
        { symbol: selectedSymbol },
        { showLoading: false }
      ).toPromise();

      if (response?.data?.feeds) {
        // Prevent duplicates by checking timestamp
        setXFeedItems(prevItems => {
          const existingTimestamps = new Set(prevItems.map(item => item.timestamp));
          const newItems = response.data.feeds.filter((item: XFeedItem) => !existingTimestamps.has(item.timestamp));

          // Combine and sort by timestamp (newest first)
          const combined = [...newItems, ...prevItems]
            .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
            .slice(0, 50); // Keep only latest 50 items

          return combined;
        });

        setLastXFeedRefresh(new Date());
        setXFeedCountdown(1800); // Reset to 30 minutes
      }
    } catch (error) {
      console.error('Error refreshing X feed:', error);
    } finally {
      setXFeedLoading(false);
    }
  };

  // X Feed countdown timer — disabled (X Feed not rendered, avoid unnecessary API calls)
  // Enable this when X Feed is added back to the layout
  // useEffect(() => {
  //   const timer = setInterval(() => {
  //     setXFeedCountdown(prev => {
  //       if (prev <= 1) { refreshXFeed(); return 1800; }
  //       return prev - 1;
  //     });
  //   }, 1000);
  //   return () => clearInterval(timer);
  // }, [selectedSymbol]);

  // Format countdown time
  const formatXFeedCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Real-time X Feed Component
  const renderXFeed = () => (
    <div className="box py-3 px-3" style={{ height: '100%', maxHeight: '400px', overflowY: 'auto' }}>
      <div className="level mb-2">
        <div className="level-left">
          <div className="level-item">
            <p className="heading is-size-7 mb-0">Market Updates from X</p>
          </div>
        </div>
        <div className="level-right">
          <div className="level-item">
            <button
              className={`button is-small is-info ${xFeedLoading ? 'is-loading' : ''}`}
              onClick={refreshXFeed}
              disabled={xFeedLoading}
            >
              <span className="icon is-small">
                <i className="fa fa-refresh"></i>
              </span>
              <span className="is-size-7">{formatXFeedCountdown(xFeedCountdown)}</span>
            </button>
          </div>
        </div>
      </div>

      {xFeedItems.length === 0 && !xFeedLoading && (
        <div className="has-text-centered py-4">
          <p className="is-size-7 has-text-grey">No updates yet. Click refresh to fetch latest market news.</p>
        </div>
      )}

      {xFeedItems.map((item) => (
        <div key={item.id} className="box mb-2 py-2 px-3" style={{ backgroundColor: '#f9f9f9' }}>
          <div className="is-size-7 mb-1" style={{ lineHeight: '1.4' }}>
            {item.text}
          </div>
          <div className="level mb-0 mt-1">
            <div className="level-left">
              <div className="level-item">
                <span className="tag is-small is-light">
                  <i className="fa fa-clock-o mr-1"></i>
                  {new Date(item.timestamp).toLocaleString('en-IN', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
              {item.sentiment && (
                <div className="level-item">
                  <span className={`tag is-small ${
                    item.sentiment === 'positive' ? 'is-success' :
                    item.sentiment === 'negative' ? 'is-danger' :
                    'is-warning'
                  }`}>
                    {item.sentiment === 'positive' ? '📈 Bullish' :
                     item.sentiment === 'negative' ? '📉 Bearish' : '➡️ Neutral'}
                  </span>
                </div>
              )}
            </div>
            {item.url && (
              <div className="level-right">
                <div className="level-item">
                  <a href={item.url} target="_blank" rel="noopener noreferrer" className="is-size-7">
                    <i className="fa fa-external-link"></i>
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  // Derive S/R from oiLevels (computed by throttled computeOILevels)
  const calculateSupportResistance = () => {
    const supports = oiLevels
      .filter(l => l.type === 'oi_support')
      .map(l => ({ strike: l.level, oi: l.oi || 0, strength: (l.oi || 0) > 1000000 ? 'Strong' : (l.oi || 0) > 500000 ? 'Medium' : 'Weak' }));
    const resistances = oiLevels
      .filter(l => l.type === 'oi_resistance')
      .map(l => ({ strike: l.level, oi: l.oi || 0, strength: (l.oi || 0) > 1000000 ? 'Strong' : (l.oi || 0) > 500000 ? 'Medium' : 'Weak' }));
    return { supports, resistances };
  };

  // Get key strikes for compact view
  const getKeyStrikes = () => {
    if (!marketData || optionChain.length === 0) return [];

    // Find ATM strike
    const atmIndex = optionChain.findIndex(row =>
      Math.abs(row.strike - marketData.spot_price) < 50
    );

    const closestIndex = atmIndex >= 0 ? atmIndex :
      optionChain.reduce((closest, row, idx) => {
        const currentDiff = Math.abs(row.strike - marketData.spot_price);
        const closestDiff = Math.abs(optionChain[closest].strike - marketData.spot_price);
        return currentDiff < closestDiff ? idx : closest;
      }, 0);

    // Get ATM ±2 strikes
    const startIndex = Math.max(0, closestIndex - 2);
    const endIndex = Math.min(optionChain.length, closestIndex + 3);
    const atmStrikes = optionChain.slice(startIndex, endIndex);

    // Find highest CE (Call) OI strike
    const highestCEOI = [...optionChain].sort((a, b) => b.callOI - a.callOI)[0];

    // Find highest PE (Put) OI strike
    const highestPEOI = [...optionChain].sort((a, b) => b.putOI - a.putOI)[0];

    // Combine and remove duplicates
    const keyStrikesMap = new Map();
    [...atmStrikes, highestCEOI, highestPEOI].forEach(strike => {
      if (strike) keyStrikesMap.set(strike.strike, strike);
    });

    return Array.from(keyStrikesMap.values()).sort((a, b) => a.strike - b.strike);
  };

  // Option Chain Component
  const renderOptionChain = () => {
    const { supports, resistances } = calculateSupportResistance();
    const keyStrikes = getKeyStrikes();
    const highestCEOI = optionChain.length > 0 ? [...optionChain].sort((a, b) => b.callOI - a.callOI)[0]?.strike : null;
    const highestPEOI = optionChain.length > 0 ? [...optionChain].sort((a, b) => b.putOI - a.putOI)[0]?.strike : null;

    return (
      <div className="box py-2 px-2" style={{ position: 'relative' }}>
        <button
          className="delete is-small"
          style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', zIndex: 10 }}
          onClick={() => setShowOptionChainPanel(false)}
          title="Close Option Chain"
        ></button>
        <p className="heading is-size-7 mb-2 has-text-centered">
          <span className="icon is-small mr-1"><i className="fa fa-table"></i></span>
          Option Chain (Key Strikes)
          <span className="tag is-info is-small ml-2">{optionChain.length} total</span>
        </p>
      <CollapsiblePanel
        title=""
        icon=""
        defaultOpen={true}
        badge=""
        badgeColor="is-info"
      >
        <div className="mb-2">
          <button
            className="button is-small is-info is-outlined is-fullwidth"
            onClick={() => setShowAllStrikesModal(true)}
          >
            <span className="icon is-small"><i className="fa fa-expand"></i></span>
            <span>View All Strikes</span>
          </button>
        </div>

        <div className="table-container">
          <table className="table is-fullwidth is-striped is-hoverable is-narrow">
            <thead>
              <tr>
                <th colSpan={4} className="has-text-centered has-background-success-light has-text-success py-1 is-size-7">CALLS</th>
                <th className="has-text-centered has-background-grey-lighter py-1 is-size-7">STRIKE</th>
                <th colSpan={4} className="has-text-centered has-background-danger-light has-text-danger py-1 is-size-7">PUTS</th>
              </tr>
              <tr style={{ fontSize: '0.65rem' }}>
                <th className="py-1">OI</th>
                <th className="py-1">ΔOI</th>
                <th className="py-1">Vol</th>
                <th className="py-1">LTP</th>
                <th className="has-text-centered has-text-weight-bold py-1">Price</th>
                <th className="py-1">LTP</th>
                <th className="py-1">Vol</th>
                <th className="py-1">ΔOI</th>
                <th className="py-1">OI</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                if (!marketData || keyStrikes.length === 0) {
                  return (
                    <tr>
                      <td colSpan={9} className="has-text-centered py-3">
                        <span className="is-size-7">No data available</span>
                      </td>
                    </tr>
                  );
                }

                return keyStrikes.map((row, idx) => {
                  const isATM = Math.abs(row.strike - marketData.spot_price) < 50;
                  const isHighestCE = row.strike === highestCEOI;
                  const isHighestPE = row.strike === highestPEOI;
                  const { callChange, putChange } = calculateOIChange(row.strike);

                  return (
                    <tr
                      key={idx}
                      className={isATM ? 'has-background-warning-light has-text-weight-bold' : ''}
                      style={{ fontSize: '0.7rem' }}
                    >
                      <td className="has-text-success py-1">
                        {row.callOI ? ((row.callOI / 1000).toFixed(0) + 'K') : '0'}
                        {isHighestCE && <span className="ml-1">🔥</span>}
                      </td>
                      <td className="py-1" style={{ fontSize: '0.65rem' }}>
                        {callChange !== 0 && (
                          <span className={callChange > 0 ? 'has-text-success' : 'has-text-danger'}>
                            {callChange > 0 ? '↑' : '↓'}{Math.abs(callChange / 1000).toFixed(0)}K
                          </span>
                        )}
                        {callChange === 0 && <span className="has-text-grey-light">-</span>}
                      </td>
                      <td className="has-text-success-dark py-1">{row.callVolume ? ((row.callVolume / 1000).toFixed(0) + 'K') : '0'}</td>
                      <td className="has-text-weight-bold has-text-success py-1">{row.callLTP || '0'}</td>
                      <td className="has-text-centered has-text-weight-bold has-background-grey-lighter py-1">
                        {row.strike}
                        {isATM && <span className="ml-1">📍</span>}
                      </td>
                      <td className="has-text-weight-bold has-text-danger py-1">{row.putLTP || '0'}</td>
                      <td className="has-text-danger-dark py-1">{row.putVolume ? ((row.putVolume / 1000).toFixed(0) + 'K') : '0'}</td>
                      <td className="py-1" style={{ fontSize: '0.65rem' }}>
                        {putChange !== 0 && (
                          <span className={putChange > 0 ? 'has-text-success' : 'has-text-danger'}>
                            {putChange > 0 ? '↑' : '↓'}{Math.abs(putChange / 1000).toFixed(0)}K
                          </span>
                        )}
                        {putChange === 0 && <span className="has-text-grey-light">-</span>}
                      </td>
                      <td className="has-text-danger py-1">
                        {row.putOI ? ((row.putOI / 1000).toFixed(0) + 'K') : '0'}
                        {isHighestPE && <span className="ml-1">🔥</span>}
                      </td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>

        <div className="notification is-info is-light py-2 px-3 mt-2 mb-2">
          <p className="is-size-7 mb-0">
            <strong>📍</strong> ATM | <strong>🔥</strong> Highest OI | <strong>ΔOI:</strong> OI Change (5min) | <strong>↑↓</strong> Direction | <strong>OI:</strong> Open Interest | <strong>Vol:</strong> Volume | <strong>LTP:</strong> Last Price
            {oiChangeTime && <span className="ml-2 has-text-grey">Last Update: {oiChangeTime.toLocaleTimeString()}</span>}
          </p>
        </div>

        {/* Support & Resistance Levels */}
        {(supports.length > 0 || resistances.length > 0) && (
          <div className="columns is-mobile mb-0" style={{ margin: '0' }}>
            {/* Support Levels */}
            <div className="column is-6" style={{ padding: '0 0.25rem 0 0' }}>
              <div className="box has-background-success-light py-2 px-3 mb-0">
                <p className="has-text-weight-bold is-size-7 mb-2 has-text-success">
                  <span className="icon is-small"><i className="fa fa-arrow-down"></i></span>
                  Support Levels (Put OI)
                </p>
                {supports.length > 0 ? (
                  <div className="content is-small">
                    {supports.map((level, idx) => (
                      <div key={idx} className="is-flex is-justify-content-space-between mb-1" style={{ fontSize: '0.7rem' }}>
                        <span className="has-text-weight-bold">{level.strike}</span>
                        <span>
                          <span className={`tag is-small ${
                            level.strength === 'Strong' ? 'is-success' :
                            level.strength === 'Medium' ? 'is-warning' : 'is-light'
                          }`}>
                            {level.strength}
                          </span>
                          <span className="ml-1 has-text-grey">{((level.oi / 1000000).toFixed(2))}M</span>
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="is-size-7 has-text-grey">No supports found</p>
                )}
              </div>
            </div>

            {/* Resistance Levels */}
            <div className="column is-6" style={{ padding: '0 0 0 0.25rem' }}>
              <div className="box has-background-danger-light py-2 px-3 mb-0">
                <p className="has-text-weight-bold is-size-7 mb-2 has-text-danger">
                  <span className="icon is-small"><i className="fa fa-arrow-up"></i></span>
                  Resistance Levels (Call OI)
                </p>
                {resistances.length > 0 ? (
                  <div className="content is-small">
                    {resistances.map((level, idx) => (
                      <div key={idx} className="is-flex is-justify-content-space-between mb-1" style={{ fontSize: '0.7rem' }}>
                        <span className="has-text-weight-bold">{level.strike}</span>
                        <span>
                          <span className={`tag is-small ${
                            level.strength === 'Strong' ? 'is-danger' :
                            level.strength === 'Medium' ? 'is-warning' : 'is-light'
                          }`}>
                            {level.strength}
                          </span>
                          <span className="ml-1 has-text-grey">{((level.oi / 1000000).toFixed(2))}M</span>
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="is-size-7 has-text-grey">No resistances found</p>
                )}
              </div>
            </div>
          </div>
        )}
      </CollapsiblePanel>
      </div>
    );
  };

  return (
    <div className="section" style={{ padding: '1rem' }}>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
      <div className="container is-fluid">
        {/* Compact Header */}
        <div className="level mb-3" style={{ backgroundColor: '#f5f5f5', padding: '0.75rem 1rem', borderRadius: '4px' }}>
          <div className="level-left">
            <div className="level-item">
              <span className="has-text-weight-bold is-size-6 mr-3">Options Trading Guide - {selectedSymbol}</span>
              {marketData && (
                <>
                  <span className="mr-3">
                    <span className="has-text-weight-bold">Spot:</span> {marketData.spot_price.toFixed(2)}
                    <span className={`ml-1 ${marketData.change >= 0 ? 'has-text-success' : 'has-text-danger'}`}>
                      ({marketData.change >= 0 ? '+' : ''}{marketData.change.toFixed(2)})
                    </span>
                  </span>
                  <span className="mr-3">
                    <span className="has-text-weight-bold">PCR:</span> {pcr.toFixed(2)}
                  </span>
                  <span className="mr-3">
                    <span className="has-text-weight-bold">Max Pain:</span> {maxPain.toFixed(0)}
                  </span>
                  <div className="select is-small mr-3">
                    <select
                      value={selectedExpiry}
                      onChange={(e) => setSelectedExpiry(e.target.value)}
                      style={{ fontSize: '0.875rem' }}
                    >
                      <option value="">Select Expiry</option>
                      {availableExpiries.map((expiry) => (
                        <option key={expiry} value={expiry}>
                          {expiry}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="level-right">
            {/* WebSocket Toggle */}
            <div className="level-item">
              {wsEnabled ? (
                <button
                  onClick={stopWebSocket}
                  className="button is-small is-success"
                  title={wsMessage}
                >
                  <span className="icon is-small">
                    <i className="fa fa-wifi"></i>
                  </span>
                  <span>Live</span>
                  <span className="ml-1" style={{ animation: 'pulse 1.5s infinite' }}>●</span>
                </button>
              ) : (
                <button
                  onClick={startWebSocket}
                  disabled={wsStatus === 'connecting'}
                  className={`button is-small is-light ${wsStatus === 'connecting' ? 'is-loading' : ''}`}
                  title="Start live data streaming"
                >
                  <span className="icon is-small">
                    <i className="fa fa-wifi"></i>
                  </span>
                  <span>Start Live</span>
                </button>
              )}
              {wsMessage && wsStatus !== 'connected' && (
                <span className="is-size-7 has-text-grey ml-2">{wsMessage}</span>
              )}
            </div>

            {!wsEnabled && (
              <div className="level-item">
                <label className="checkbox mr-3" style={{ fontSize: '0.875rem' }}>
                  <input
                    type="checkbox"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    className="mr-1"
                  />
                  Auto ({formatTime(countdown)})
                </label>
              </div>
            )}

            <div className="level-item">
              <button
                onClick={refreshAll}
                disabled={loading || wsEnabled}
                className={`button is-small is-info ${loading ? 'is-loading' : ''}`}
                title={wsEnabled ? 'Disabled during live streaming' : 'Refresh data'}
              >
                <span className="icon is-small"><i className="fa fa-refresh"></i></span>
                <span>Refresh</span>
              </button>
            </div>

            <div className="level-item">
              <button
                onClick={() => setShowFetchHistoricalModal(true)}
                className="button is-small is-warning"
                title="Fetch historical price data"
              >
                <span className="icon is-small"><i className="fa fa-download"></i></span>
                <span>Fetch Historical</span>
              </button>
            </div>

            <div className="level-item">
              <button
                onClick={() => setShowStrategyBuilderModal(true)}
                className="button is-small is-info"
                title="Open Strategy Builder"
              >
                <span className="icon is-small"><i className="fa fa-calculator"></i></span>
                <span>Strategy</span>
              </button>
            </div>
          </div>
        </div>

        {loading && optionChain.length === 0 ? (
          <div className="box has-text-centered py-5">
            <button className="button is-loading is-large is-white"></button>
            <p className="mt-3">Loading...</p>
          </div>
        ) : (
          <>
            {/* Hidden TechnicalIndicators - loads data only */}
            <div style={{ display: 'none' }}>
              <TechnicalIndicators
                symbol={selectedSymbol}
                onDataLoaded={(data) => setTechnicalIndicators(data)}
              />
            </div>

            {/* Hidden Market Overview Cards - loads data only */}
            <div style={{ display: 'none' }}>
              {renderMarketOverviewCards()}
            </div>

            {/* Quick Action Buttons - Horizontal Layout */}
            {renderQuickActionButtons()}

            <div className="columns" style={{ gap: '0.5rem' }}>
              {/* LEFT COLUMN - Chart (80%) */}
              <div className="column is-four-fifths" style={{ padding: '0 0.25rem' }}>
                {/* Advanced Trading Chart */}
                <DashboardErrorBoundary name="Trading Chart">
                  <div className="box py-2 px-2">
                    <AdvancedTradingChart
                      symbol={selectedSymbol}
                      height={550}
                      wsEnabled={wsEnabled}
                      livePrice={marketData?.spot_price || 0}
                      supportLevels={aiAnalysis?.support_levels || []}
                      resistanceLevels={aiAnalysis?.resistance_levels || []}
                      oiLevels={oiLevels}
                      showVWAP={true}
                    />
                  </div>
                </DashboardErrorBoundary>

                {/* Put/Call OI Chart */}
                <DashboardErrorBoundary name="OI Chart">
                  {optionChain.length > 0 && marketData && (
                    <OIChart
                      optionChain={optionChain}
                      spotPrice={marketData.spot_price}
                      height={350}
                      oiChangeData={oiChangeData}
                      pcrTrend={pcrTrend}
                    />
                  )}
                </DashboardErrorBoundary>

                {/* Confluence Alerts */}
                {confluenceAlerts.length > 0 && (
                  <div style={{
                    backgroundColor: '#1e222d',
                    borderRadius: '4px',
                    padding: '0.5rem 0.75rem',
                    marginTop: '0.5rem',
                  }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#d1d4dc', marginBottom: '0.35rem' }}>
                      OI + Price Confluence
                    </div>
                    {confluenceAlerts.map((alert, i) => (
                      <div key={i} style={{
                        fontSize: '0.7rem',
                        padding: '0.25rem 0.5rem',
                        marginBottom: '0.25rem',
                        borderRadius: '3px',
                        borderLeft: `3px solid ${alert.type === 'bullish' ? '#26a69a' : alert.type === 'bearish' ? '#ef5350' : '#ffd54f'}`,
                        backgroundColor: alert.type === 'bullish' ? 'rgba(38,166,154,0.1)' : alert.type === 'bearish' ? 'rgba(239,83,80,0.1)' : 'rgba(255,213,79,0.1)',
                        color: '#d1d4dc',
                      }}>
                        <span style={{ color: alert.type === 'bullish' ? '#26a69a' : alert.type === 'bearish' ? '#ef5350' : '#ffd54f', fontWeight: 600 }}>
                          {alert.type === 'bullish' ? 'BULL' : alert.type === 'bearish' ? 'BEAR' : 'INFO'}
                        </span>
                        {' '}{alert.message}
                      </div>
                    ))}
                    {oiChangeTime && (
                      <div style={{ fontSize: '0.6rem', color: '#555', marginTop: '0.25rem' }}>
                        OI change since: {oiChangeTime.toLocaleTimeString('en-IN')}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* RIGHT COLUMN - AI Chat (20%) */}
              <div className="column is-one-fifth" style={{ padding: '0 0.25rem' }}>
                <DashboardErrorBoundary name="AI Chat">
                  <OptionsChatBot
                    symbol={selectedSymbol}
                    spotPrice={marketData?.spot_price || 0}
                    pcr={pcr}
                    optionChain={optionChain}
                    technicalIndicators={technicalIndicators}
                    expiryDate={selectedExpiry}
                    oiChangeData={oiChangeData}
                    confluenceAlerts={confluenceAlerts}
                  />
                </DashboardErrorBoundary>
              </div>
            </div>
          </>
        )}

        {/* Compact Footer */}
        <div className="has-text-centered mt-2 py-2" style={{ fontSize: '0.65rem', color: '#999' }}>
          <p className="mb-0">Disclaimer: Trading involves risk. For educational purposes only. | Powered by Kite API & Grok AI</p>
        </div>
      </div>

      {/* Analysis Details Modal */}
      {showAnalysisModal && aiAnalysis && (
        <div className="modal is-active">
          <div className="modal-background" onClick={() => setShowAnalysisModal(false)}></div>
          <div className="modal-card" style={{ maxWidth: '800px', width: '95%' }}>
            <header className="modal-card-head py-3">
              <p className="modal-card-title is-size-5">
                <span className="icon"><i className="fa fa-brain"></i></span>
                <span>AI Analysis Details - {aiAnalysis.symbol}</span>
              </p>
              <button className="delete" aria-label="close" onClick={() => setShowAnalysisModal(false)}></button>
            </header>
            <section className="modal-card-body">
              {/* Summary Cards */}
              <div className="columns is-mobile mb-4">
                <div className="column is-4">
                  <div className={`notification py-3 px-3 mb-0 ${getTrendColor(aiAnalysis.trend)} is-light has-text-centered`}>
                    <p className="is-size-7 mb-1 has-text-weight-bold">TREND</p>
                    <p className="is-size-4 mb-0">
                      {getTrendIcon(aiAnalysis.trend)} {aiAnalysis.trend.toUpperCase()}
                    </p>
                  </div>
                </div>
                <div className="column is-4">
                  <div className={`notification py-3 px-3 mb-0 ${
                    aiAnalysis.trading_signal === 'BUY' ? 'is-success' :
                    aiAnalysis.trading_signal === 'SELL' ? 'is-danger' :
                    'is-warning'
                  } is-light has-text-centered`}>
                    <p className="is-size-7 mb-1 has-text-weight-bold">TRADING SIGNAL</p>
                    <p className="is-size-4 mb-0 has-text-weight-bold">{aiAnalysis.trading_signal}</p>
                  </div>
                </div>
                <div className="column is-4">
                  <div className={`notification py-3 px-3 mb-0 ${getSentimentColor(aiAnalysis.sentiment)} is-light has-text-centered`}>
                    <p className="is-size-7 mb-1 has-text-weight-bold">SENTIMENT</p>
                    <p className="is-size-4 mb-0">
                      {getSentimentIcon(aiAnalysis.sentiment)} {aiAnalysis.sentiment?.toUpperCase() || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Sentiment Reason */}
              {aiAnalysis.sentiment_reason && (
                <div className="notification is-light py-2 px-3 mb-3">
                  <strong className="is-size-7">Sentiment Analysis:</strong>
                  <p className="is-size-7 mb-0 mt-1">{aiAnalysis.sentiment_reason}</p>
                </div>
              )}

              {/* OI Analysis */}
              {aiAnalysis.oi_analysis && (
                <div className="box py-3 px-3 mb-3">
                  <h4 className="title is-6 mb-2">
                    <span className="icon is-small"><i className="fa fa-bar-chart"></i></span>
                    <span>OI Analysis</span>
                    <span className={`tag is-small ml-2 ${
                      aiAnalysis.oi_analysis.oi_change_signal === 'bullish' ? 'is-success' :
                      aiAnalysis.oi_analysis.oi_change_signal === 'bearish' ? 'is-danger' :
                      'is-warning'
                    }`}>
                      {aiAnalysis.oi_analysis.oi_change_signal?.toUpperCase() || 'NEUTRAL'}
                    </span>
                  </h4>
                  <div className="columns is-mobile">
                    <div className="column is-6">
                      <div className="notification is-success is-light py-2 px-3 mb-0">
                        <strong className="is-size-7">Call OI Buildup:</strong>
                        <p className="is-size-7 mb-0">{aiAnalysis.oi_analysis.call_oi_buildup}</p>
                      </div>
                    </div>
                    <div className="column is-6">
                      <div className="notification is-danger is-light py-2 px-3 mb-0">
                        <strong className="is-size-7">Put OI Buildup:</strong>
                        <p className="is-size-7 mb-0">{aiAnalysis.oi_analysis.put_oi_buildup}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Support & Resistance */}
              <div className="columns mb-3">
                <div className="column is-6">
                  <div className="box has-background-success-light py-2 px-3 mb-0">
                    <strong className="is-size-7">Support Levels:</strong>
                    <ul className="mt-1 mb-0" style={{ fontSize: '0.8rem' }}>
                      {aiAnalysis.support_levels.map((level, idx) => (
                        <li key={idx} className="mb-1">
                          <strong>{level.level}</strong>
                          <span className={`tag is-small ml-1 ${
                            level.strength === 'strong' ? 'is-success' :
                            level.strength === 'moderate' ? 'is-warning' :
                            'is-light'
                          }`}>{level.strength}</span>
                          <p className="is-size-7 has-text-grey mb-0">{level.reason}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="column is-6">
                  <div className="box has-background-danger-light py-2 px-3 mb-0">
                    <strong className="is-size-7">Resistance Levels:</strong>
                    <ul className="mt-1 mb-0" style={{ fontSize: '0.8rem' }}>
                      {aiAnalysis.resistance_levels.map((level, idx) => (
                        <li key={idx} className="mb-1">
                          <strong>{level.level}</strong>
                          <span className={`tag is-small ml-1 ${
                            level.strength === 'strong' ? 'is-danger' :
                            level.strength === 'moderate' ? 'is-warning' :
                            'is-light'
                          }`}>{level.strength}</span>
                          <p className="is-size-7 has-text-grey mb-0">{level.reason}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Technical Analysis */}
              {aiAnalysis.technical_analysis && (
                <div className="box py-3 px-3 mb-3">
                  <h4 className="title is-6 mb-2">
                    <span className="icon is-small"><i className="fa fa-line-chart"></i></span>
                    <span>Technical Analysis</span>
                    <span className={`tag is-small ml-2 ${
                      aiAnalysis.technical_analysis.overall_technical_signal === 'bullish' ? 'is-success' :
                      aiAnalysis.technical_analysis.overall_technical_signal === 'bearish' ? 'is-danger' :
                      'is-warning'
                    }`}>
                      {aiAnalysis.technical_analysis.overall_technical_signal?.toUpperCase() || 'NEUTRAL'}
                    </span>
                  </h4>
                  <div className="content is-small">
                    {aiAnalysis.technical_analysis.rsi_interpretation && (
                      <p className="mb-1"><strong>RSI:</strong> {aiAnalysis.technical_analysis.rsi_interpretation}</p>
                    )}
                    {aiAnalysis.technical_analysis.macd_interpretation && (
                      <p className="mb-1"><strong>MACD:</strong> {aiAnalysis.technical_analysis.macd_interpretation}</p>
                    )}
                    {aiAnalysis.technical_analysis.moving_avg_interpretation && (
                      <p className="mb-0"><strong>Moving Averages:</strong> {aiAnalysis.technical_analysis.moving_avg_interpretation}</p>
                    )}
                  </div>
                </div>
              )}

              {/* PCR Interpretation */}
              <div className="notification is-light py-2 px-3 mb-3">
                <strong className="is-size-7">PCR Interpretation:</strong>
                <p className="is-size-7 mb-0 mt-1">{aiAnalysis.pcr_interpretation}</p>
              </div>

              {/* Key Insights */}
              <div className="box py-2 px-3 mb-3">
                <strong className="is-size-7">Key Insights:</strong>
                <ul className="mt-1 mb-0" style={{ fontSize: '0.8rem' }}>
                  {aiAnalysis.key_insights.map((insight, idx) => (
                    <li key={idx}>{insight}</li>
                  ))}
                </ul>
              </div>

              {/* Risk Factors */}
              <div className="notification is-warning is-light py-2 px-3 mb-3">
                <strong className="is-size-7">Risk Factors:</strong>
                <ul className="mt-1 mb-0" style={{ fontSize: '0.8rem' }}>
                  {aiAnalysis.risk_factors.map((risk, idx) => (
                    <li key={idx}>{risk}</li>
                  ))}
                </ul>
              </div>

              {/* Confidence & Timestamp */}
              <div className="has-text-centered">
                <p className="has-text-grey" style={{ fontSize: '0.75rem' }}>
                  <strong>Confidence:</strong> {(aiAnalysis.confidence * 100).toFixed(0)}% |
                  <strong> Analysis Time:</strong> {new Date(aiAnalysis.analysis_timestamp).toLocaleString()}
                </p>
                <p className="has-text-grey is-size-7 mt-1">
                  Based on: Price Action, Option Chain Data, OI Analysis, Technical Indicators
                </p>
              </div>
            </section>
            <footer className="modal-card-foot py-2">
              <button className="button is-info" onClick={() => setShowAnalysisModal(false)}>Close</button>
            </footer>
          </div>
        </div>
      )}

      {/* Technical Indicators Modal */}
      {showTechnicalIndicatorsModal && (
        <div className="modal is-active">
          <div className="modal-background" onClick={() => setShowTechnicalIndicatorsModal(false)}></div>
          <div className="modal-card" style={{ width: '90%', maxWidth: '800px' }}>
            <header className="modal-card-head">
              <p className="modal-card-title">
                <span className="icon"><i className="fa fa-line-chart"></i></span>
                <span>Technical Indicators - {selectedSymbol}</span>
              </p>
              <button
                className="delete"
                aria-label="close"
                onClick={() => setShowTechnicalIndicatorsModal(false)}
              ></button>
            </header>
            <section className="modal-card-body">
              <TechnicalIndicators
                symbol={selectedSymbol}
                onDataLoaded={(data) => setTechnicalIndicators(data)}
              />
            </section>
            <footer className="modal-card-foot">
              <button className="button" onClick={() => setShowTechnicalIndicatorsModal(false)}>Close</button>
            </footer>
          </div>
        </div>
      )}

      {/* Strategy Builder Modal */}
      {showStrategyBuilderModal && (
        <div className="modal is-active">
          <div className="modal-background" onClick={() => setShowStrategyBuilderModal(false)}></div>
          <div className="modal-card" style={{ width: '95%', maxWidth: '1000px' }}>
            <header className="modal-card-head">
              <p className="modal-card-title">
                <span className="icon"><i className="fa fa-calculator"></i></span>
                <span>Strategy Builder - {selectedSymbol}</span>
              </p>
              <button
                className="delete"
                aria-label="close"
                onClick={() => setShowStrategyBuilderModal(false)}
              ></button>
            </header>
            <section className="modal-card-body">
              <StrategyBuilder
                optionChain={optionChain}
                spotPrice={marketData?.spot_price || 0}
                recommendedStrategy={aiAnalysis?.recommended_strategy}
                lotSize={selectedSymbol === 'NIFTY' ? 50 : selectedSymbol === 'BANKNIFTY' ? 15 : 40}
                oiLevels={oiLevels.filter(l => l.type !== 'max_pain').map(l => ({ strike: l.level, type: l.type as 'oi_support' | 'oi_resistance', oi: l.oi || 0 }))}
              />
            </section>
            <footer className="modal-card-foot">
              <button className="button" onClick={() => setShowStrategyBuilderModal(false)}>Close</button>
            </footer>
          </div>
        </div>
      )}

      {/* Market News Modal */}
      {showNewsModal && (
        <div className="modal is-active">
          <div className="modal-background" onClick={() => setShowNewsModal(false)}></div>
          <div className="modal-card" style={{ width: '90%', maxWidth: '900px' }}>
            <header className="modal-card-head">
              <p className="modal-card-title">
                <span className="icon"><i className="fa fa-newspaper-o"></i></span>
                <span>Market News - {selectedSymbol}</span>
              </p>
              <button
                className="delete"
                aria-label="close"
                onClick={() => setShowNewsModal(false)}
              ></button>
            </header>
            <section className="modal-card-body">
              {marketNews ? (
                <>
                  {/* Overall Sentiment Summary */}
                  {marketNews.sentiment_summary && (
                    <div className="notification is-info is-light mb-3">
                      <p className="is-size-6 has-text-weight-semibold mb-2">Overall Market Sentiment</p>
                      <p className="is-size-7">{marketNews.sentiment_summary}</p>
                    </div>
                  )}

                  {/* Global News */}
                  {marketNews.global_news && marketNews.global_news.length > 0 && (
                    <div className="mb-4">
                      <h3 className="title is-6 mb-3">
                        🌍 Global News
                        <span className={`tag is-small ml-2 ${
                          marketNews.overall_global_sentiment === 'positive' ? 'is-success' :
                          marketNews.overall_global_sentiment === 'negative' ? 'is-danger' :
                          'is-warning'
                        }`}>
                          {marketNews.overall_global_sentiment?.toUpperCase() || 'N/A'}
                        </span>
                      </h3>
                      {marketNews.global_news.map((item: any, idx: number) => (
                        <div key={idx} className="notification is-light py-2 px-3 mb-2">
                          <div className="level mb-1">
                            <div className="level-left">
                              <div className="level-item">
                                <p className="is-size-7 has-text-weight-semibold mb-0">{item.headline}</p>
                              </div>
                            </div>
                            {item.sentiment && (
                              <div className="level-right">
                                <div className="level-item">
                                  <span className={`tag is-small ${
                                    item.sentiment === 'positive' ? 'is-success' :
                                    item.sentiment === 'negative' ? 'is-danger' :
                                    'is-warning'
                                  }`}>
                                    {item.sentiment}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                          <p className="is-size-7 has-text-grey mb-1">{item.summary}</p>
                          {item.url && (
                            <a href={item.url} target="_blank" rel="noopener noreferrer" className="is-size-7">
                              Read more →
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Indian News */}
                  {marketNews.indian_news && marketNews.indian_news.length > 0 && (
                    <div className="mb-4">
                      <h3 className="title is-6 mb-3">
                        🇮🇳 Indian Market News
                        <span className={`tag is-small ml-2 ${
                          marketNews.overall_indian_sentiment === 'positive' ? 'is-success' :
                          marketNews.overall_indian_sentiment === 'negative' ? 'is-danger' :
                          'is-warning'
                        }`}>
                          {marketNews.overall_indian_sentiment?.toUpperCase() || 'N/A'}
                        </span>
                      </h3>
                      {marketNews.indian_news.map((item: any, idx: number) => (
                        <div key={idx} className="notification is-light py-2 px-3 mb-2">
                          <div className="level mb-1">
                            <div className="level-left">
                              <div className="level-item">
                                <p className="is-size-7 has-text-weight-semibold mb-0">{item.headline}</p>
                              </div>
                            </div>
                            {item.sentiment && (
                              <div className="level-right">
                                <div className="level-item">
                                  <span className={`tag is-small ${
                                    item.sentiment === 'positive' ? 'is-success' :
                                    item.sentiment === 'negative' ? 'is-danger' :
                                    'is-warning'
                                  }`}>
                                    {item.sentiment}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                          <p className="is-size-7 has-text-grey mb-1">{item.summary}</p>
                          {item.url && (
                            <a href={item.url} target="_blank" rel="noopener noreferrer" className="is-size-7">
                              Read more →
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Timestamp */}
                  {marketNews.fetched_at && (
                    <p className="has-text-grey has-text-right is-size-7">
                      Updated: {new Date(marketNews.fetched_at).toLocaleString()}
                    </p>
                  )}
                </>
              ) : (
                <div className="notification has-text-centered py-5">
                  <p className="is-size-6 mb-3">No news data available</p>
                  <p className="is-size-7 has-text-grey">Click "Fetch News" in the sidebar to load latest market news</p>
                </div>
              )}
            </section>
            <footer className="modal-card-foot">
              <button
                onClick={fetchMarketNews}
                disabled={newsStatus === 'pending' || newsStatus === 'processing'}
                className={`button is-info ${(newsStatus === 'pending' || newsStatus === 'processing') ? 'is-loading' : ''}`}
              >
                <span className="icon is-small"><i className="fa fa-refresh"></i></span>
                <span>Refresh News</span>
              </button>
              <button className="button" onClick={() => setShowNewsModal(false)}>Close</button>
            </footer>
          </div>
        </div>
      )}

      {/* AI Insights Modal */}
      {showAIInsightsModal && (
        <div className="modal is-active">
          <div className="modal-background" onClick={() => setShowAIInsightsModal(false)}></div>
          <div className="modal-card" style={{ maxWidth: '800px', width: '95%' }}>
            <header className="modal-card-head py-3">
              <p className="modal-card-title is-size-5">
                <span className="icon"><i className="fa fa-brain"></i></span>
                <span>AI Insights - {selectedSymbol}</span>
              </p>
              <button className="delete" aria-label="close" onClick={() => setShowAIInsightsModal(false)}></button>
            </header>
            <section className="modal-card-body">
              {aiAnalysis ? (
                <>
                  {/* Summary Cards */}
                  <div className="columns is-mobile mb-4">
                    <div className="column is-4">
                      <div className={`notification py-3 px-3 mb-0 ${getTrendColor(aiAnalysis.trend)} is-light has-text-centered`}>
                        <p className="is-size-7 mb-1 has-text-weight-bold">TREND</p>
                        <p className="is-size-4 mb-0">
                          {getTrendIcon(aiAnalysis.trend)} {aiAnalysis.trend.toUpperCase()}
                        </p>
                      </div>
                    </div>
                    <div className="column is-4">
                      <div className={`notification py-3 px-3 mb-0 ${
                        aiAnalysis.trading_signal === 'BUY' ? 'is-success' :
                        aiAnalysis.trading_signal === 'SELL' ? 'is-danger' :
                        'is-warning'
                      } is-light has-text-centered`}>
                        <p className="is-size-7 mb-1 has-text-weight-bold">TRADING SIGNAL</p>
                        <p className="is-size-4 mb-0 has-text-weight-bold">{aiAnalysis.trading_signal}</p>
                      </div>
                    </div>
                    <div className="column is-4">
                      <div className={`notification py-3 px-3 mb-0 ${getSentimentColor(aiAnalysis.sentiment)} is-light has-text-centered`}>
                        <p className="is-size-7 mb-1 has-text-weight-bold">SENTIMENT</p>
                        <p className="is-size-4 mb-0">
                          {getSentimentIcon(aiAnalysis.sentiment)} {aiAnalysis.sentiment?.toUpperCase() || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* OI Analysis */}
                  {aiAnalysis.oi_analysis && (
                    <div className="notification is-light py-3 px-3 mb-3">
                      <h4 className="is-size-6 has-text-weight-bold mb-2">Open Interest Analysis</h4>
                      <div className="content is-small">
                        <p><strong>Signal:</strong>
                          <span className={`tag ml-2 ${
                            aiAnalysis.oi_analysis.oi_change_signal === 'bullish' ? 'is-success' :
                            aiAnalysis.oi_analysis.oi_change_signal === 'bearish' ? 'is-danger' :
                            'is-warning'
                          }`}>
                            {aiAnalysis.oi_analysis.oi_change_signal?.toUpperCase() || 'NEUTRAL'}
                          </span>
                        </p>
                        <p className="is-size-7 mt-2">
                          <strong>Call OI Buildup:</strong> {aiAnalysis.oi_analysis.call_oi_buildup}
                        </p>
                        <p className="is-size-7">
                          <strong>Put OI Buildup:</strong> {aiAnalysis.oi_analysis.put_oi_buildup}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Sentiment Reason */}
                  {aiAnalysis.sentiment_reason && (
                    <div className="notification is-light py-3 px-3 mb-3">
                      <h4 className="is-size-6 has-text-weight-bold mb-2">Sentiment Analysis</h4>
                      <p className="is-size-7">{aiAnalysis.sentiment_reason}</p>
                    </div>
                  )}

                  {/* Key Insights */}
                  {aiAnalysis.key_insights && aiAnalysis.key_insights.length > 0 && (
                    <div className="notification is-info is-light py-3 px-3 mb-3">
                      <h4 className="is-size-6 has-text-weight-bold mb-2">Key Insights</h4>
                      <ul className="is-size-7">
                        {aiAnalysis.key_insights.map((insight: string, idx: number) => (
                          <li key={idx}>{insight}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Risk Factors */}
                  {aiAnalysis.risk_factors && aiAnalysis.risk_factors.length > 0 && (
                    <div className="notification is-warning is-light py-3 px-3 mb-3">
                      <h4 className="is-size-6 has-text-weight-bold mb-2">Risk Factors</h4>
                      <ul className="is-size-7">
                        {aiAnalysis.risk_factors.map((risk: string, idx: number) => (
                          <li key={idx}>{risk}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Confidence & Timestamp */}
                  <div className="has-text-centered has-text-grey mt-4">
                    <p className="is-size-7">
                      Confidence: {(aiAnalysis.confidence * 100).toFixed(0)}% |
                      Generated: {new Date(aiAnalysis.analysis_timestamp).toLocaleString()}
                    </p>
                  </div>
                </>
              ) : (
                <div className="notification has-text-centered py-5">
                  <p className="is-size-6 mb-3">No AI analysis available</p>
                  <p className="is-size-7 has-text-grey">Click "Analyze" button below to generate AI insights</p>
                </div>
              )}
            </section>
            <footer className="modal-card-foot">
              <button
                onClick={getAIAnalysis}
                disabled={analysisStatus === 'pending' || analysisStatus === 'processing'}
                className={`button is-primary ${(analysisStatus === 'pending' || analysisStatus === 'processing') ? 'is-loading' : ''}`}
              >
                <span className="icon is-small"><i className="fa fa-refresh"></i></span>
                <span>Analyze</span>
              </button>
              {analysisStatus !== 'idle' && analysisStatus !== 'completed' && (
                <span className="tag is-info">
                  {analysisStatus === 'pending' && '⏳ Queued'}
                  {analysisStatus === 'processing' && '🔄 Processing...'}
                </span>
              )}
              <button className="button" onClick={() => setShowAIInsightsModal(false)}>Close</button>
            </footer>
          </div>
        </div>
      )}

      {/* Analysis History Modal */}
      {showHistoryModal && (
        <div className="modal is-active">
          <div className="modal-background" onClick={() => setShowHistoryModal(false)}></div>
          <div className="modal-card" style={{ maxWidth: '1000px', width: '95%' }}>
            <header className="modal-card-head py-3">
              <p className="modal-card-title is-size-5">
                <span className="icon"><i className="fa fa-history"></i></span>
                <span>Analysis History ({analysisHistory.length})</span>
              </p>
              <button className="delete" aria-label="close" onClick={() => setShowHistoryModal(false)}></button>
            </header>
            <section className="modal-card-body">
              {analysisHistory.length > 0 ? (
                <div className="table-container">
                  <table className="table is-fullwidth is-striped is-hoverable">
                    <thead>
                      <tr>
                        <th>Date/Time</th>
                        <th>Expiry</th>
                        <th>Spot Price</th>
                        <th>PCR</th>
                        <th>Trend</th>
                        <th>Signal</th>
                        <th>Confidence</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysisHistory.map((history) => (
                        <tr key={history.id}>
                          <td className="is-size-7">{new Date(history.created_at).toLocaleString()}</td>
                          <td className="is-size-7">{history.expiry_date}</td>
                          <td className="is-size-7">{history.spot_price.toFixed(2)}</td>
                          <td className="is-size-7">{history.pcr.toFixed(2)}</td>
                          <td>
                            <span className={`tag is-small ${
                              history.trend === 'bullish' ? 'is-success' :
                              history.trend === 'bearish' ? 'is-danger' :
                              'is-warning'
                            }`}>
                              {history.trend}
                            </span>
                          </td>
                          <td>
                            <span className={`tag is-small ${
                              history.trading_signal === 'BUY' ? 'is-success' :
                              history.trading_signal === 'SELL' ? 'is-danger' :
                              'is-warning'
                            }`}>
                              {history.trading_signal}
                            </span>
                          </td>
                          <td className="is-size-7">{(history.confidence * 100).toFixed(0)}%</td>
                          <td>
                            <button
                              onClick={() => viewHistoricalAnalysis(history.id)}
                              className="button is-small is-primary is-outlined"
                            >
                              <span className="icon is-small">
                                <i className="fa fa-eye"></i>
                              </span>
                              <span>View</span>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="notification has-text-centered py-5">
                  <p className="is-size-6 mb-3">No analysis history available</p>
                  <p className="is-size-7 has-text-grey">Run an AI analysis to start building your history</p>
                </div>
              )}
            </section>
            <footer className="modal-card-foot">
              <button className="button" onClick={() => setShowHistoryModal(false)}>Close</button>
            </footer>
          </div>
        </div>
      )}

      {/* Price Patterns Modal */}
      {showPatternsModal && (
        <div className="modal is-active">
          <div className="modal-background" onClick={() => setShowPatternsModal(false)}></div>
          <div className="modal-card" style={{ maxWidth: '1200px', width: '95%' }}>
            <header className="modal-card-head py-3">
              <p className="modal-card-title is-size-5">
                <span className="icon"><i className="fa fa-chart-bar"></i></span>
                <span>Price Patterns - {selectedSymbol}</span>
              </p>
              <button className="delete" aria-label="close" onClick={() => setShowPatternsModal(false)}></button>
            </header>
            <section className="modal-card-body">
              <PricePatternsTable
                symbol={selectedSymbol}
                limit={50}
                autoRefresh={false}
                showCompanyColumn={false}
                showTimeframeFilter={true}
                title={`${selectedSymbol} Detected Patterns`}
              />
            </section>
            <footer className="modal-card-foot">
              <button className="button" onClick={() => setShowPatternsModal(false)}>Close</button>
            </footer>
          </div>
        </div>
      )}

      {/* All Strikes Modal */}
      {showAllStrikesModal && (
        <div className="modal is-active">
          <div className="modal-background" onClick={() => setShowAllStrikesModal(false)}></div>
          <div className="modal-card" style={{ maxWidth: '1000px', width: '95%' }}>
            <header className="modal-card-head py-3">
              <p className="modal-card-title is-size-5">
                <span className="icon"><i className="fa fa-table"></i></span>
                <span>All Option Strikes - {selectedSymbol}</span>
              </p>
              <button className="delete" aria-label="close" onClick={() => setShowAllStrikesModal(false)}></button>
            </header>
            <section className="modal-card-body">
              <div className="table-container" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                <table className="table is-fullwidth is-striped is-hoverable is-narrow">
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10 }}>
                    <tr>
                      <th colSpan={4} className="has-text-centered has-background-success-light has-text-success py-1 is-size-7">CALLS</th>
                      <th className="has-text-centered has-background-grey-lighter py-1 is-size-7">STRIKE</th>
                      <th colSpan={4} className="has-text-centered has-background-danger-light has-text-danger py-1 is-size-7">PUTS</th>
                    </tr>
                    <tr style={{ fontSize: '0.7rem' }}>
                      <th className="py-1">OI</th>
                      <th className="py-1">ΔOI</th>
                      <th className="py-1">Vol</th>
                      <th className="py-1">LTP</th>
                      <th className="has-text-centered has-text-weight-bold py-1">Price</th>
                      <th className="py-1">LTP</th>
                      <th className="py-1">Vol</th>
                      <th className="py-1">ΔOI</th>
                      <th className="py-1">OI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      if (!marketData || optionChain.length === 0) {
                        return (
                          <tr>
                            <td colSpan={9} className="has-text-centered py-3">
                              <span className="is-size-7">No data available</span>
                            </td>
                          </tr>
                        );
                      }

                      const highestCEOI = [...optionChain].sort((a, b) => b.callOI - a.callOI)[0]?.strike;
                      const highestPEOI = [...optionChain].sort((a, b) => b.putOI - a.putOI)[0]?.strike;

                      return optionChain.map((row, idx) => {
                        const isATM = Math.abs(row.strike - marketData.spot_price) < 50;
                        const isHighestCE = row.strike === highestCEOI;
                        const isHighestPE = row.strike === highestPEOI;
                        const { callChange, putChange } = calculateOIChange(row.strike);

                        return (
                          <tr
                            key={idx}
                            id={isATM ? 'atm-strike-modal' : undefined}
                            className={isATM ? 'has-background-warning-light has-text-weight-bold' : ''}
                            style={{ fontSize: '0.75rem' }}
                          >
                            <td className="has-text-success py-1">
                              {row.callOI ? ((row.callOI / 1000).toFixed(0) + 'K') : '0'}
                              {isHighestCE && <span className="ml-1">🔥</span>}
                            </td>
                            <td className="py-1" style={{ fontSize: '0.65rem' }}>
                              {callChange !== 0 && (
                                <span className={callChange > 0 ? 'has-text-success' : 'has-text-danger'}>
                                  {callChange > 0 ? '↑' : '↓'}{Math.abs(callChange / 1000).toFixed(0)}K
                                </span>
                              )}
                              {callChange === 0 && <span className="has-text-grey-light">-</span>}
                            </td>
                            <td className="has-text-success-dark py-1">{row.callVolume ? ((row.callVolume / 1000).toFixed(0) + 'K') : '0'}</td>
                            <td className="has-text-weight-bold has-text-success py-1">{row.callLTP || '0'}</td>
                            <td className="has-text-centered has-text-weight-bold has-background-grey-lighter py-1">
                              {row.strike}
                              {isATM && <span className="ml-1">📍</span>}
                            </td>
                            <td className="has-text-weight-bold has-text-danger py-1">{row.putLTP || '0'}</td>
                            <td className="has-text-danger-dark py-1">{row.putVolume ? ((row.putVolume / 1000).toFixed(0) + 'K') : '0'}</td>
                            <td className="py-1" style={{ fontSize: '0.65rem' }}>
                              {putChange !== 0 && (
                                <span className={putChange > 0 ? 'has-text-success' : 'has-text-danger'}>
                                  {putChange > 0 ? '↑' : '↓'}{Math.abs(putChange / 1000).toFixed(0)}K
                                </span>
                              )}
                              {putChange === 0 && <span className="has-text-grey-light">-</span>}
                            </td>
                            <td className="has-text-danger py-1">
                              {row.putOI ? ((row.putOI / 1000).toFixed(0) + 'K') : '0'}
                              {isHighestPE && <span className="ml-1">🔥</span>}
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>

              <div className="notification is-info is-light py-2 px-3 mt-3 mb-0">
                <p className="is-size-7 mb-0">
                  <strong>📍</strong> ATM (At-The-Money) |
                  <strong className="ml-2">🔥</strong> Highest OI |
                  <strong className="ml-2">ΔOI:</strong> OI Change (5min) |
                  <strong className="ml-2">↑↓</strong> Direction |
                  <strong className="ml-2">OI:</strong> Open Interest |
                  <strong className="ml-2">Vol:</strong> Volume |
                  <strong className="ml-2">LTP:</strong> Last Traded Price
                  {oiChangeTime && (
                    <span className="ml-3 has-text-grey">
                      Last Update: {oiChangeTime.toLocaleTimeString()}
                    </span>
                  )}
                </p>
              </div>
            </section>
            <footer className="modal-card-foot">
              <button className="button" onClick={() => setShowAllStrikesModal(false)}>Close</button>
            </footer>
          </div>
        </div>
      )}

      {/* Historical Data Fetch Modal */}
      {showFetchHistoricalModal && (
        <div className="modal is-active">
          <div className="modal-background" onClick={() => setShowFetchHistoricalModal(false)}></div>
          <div className="modal-card">
            <header className="modal-card-head">
              <p className="modal-card-title">Fetch Historical Data - {selectedSymbol}</p>
              <button
                className="delete"
                aria-label="close"
                onClick={() => setShowFetchHistoricalModal(false)}
              ></button>
            </header>
            <section className="modal-card-body">
              <FetchHistoricalForm
                symbol={selectedSymbol}
                onSuccess={() => {
                  setShowFetchHistoricalModal(false);
                  alert('Historical data fetched successfully!');
                }}
                onClose={() => setShowFetchHistoricalModal(false)}
              />
            </section>
          </div>
        </div>
      )}

    </div>
  );
};

// Historical Data Fetch Form Component
interface FetchHistoricalFormProps {
  symbol: string;
  onSuccess: () => void;
  onClose: () => void;
}

const FetchHistoricalForm: React.FC<FetchHistoricalFormProps> = ({ symbol, onSuccess, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    symbol: symbol,
    exchange: 'NSE',
    timeframe: '15m',
    from_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    to_date: new Date().toISOString().split('T')[0]
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const fromDateTime = `${formData.from_date}T00:00:00`;
    const toDateTime = `${formData.to_date}T23:59:59`;

    const params = new URLSearchParams({
      symbol: formData.symbol,
      exchange: formData.exchange,
      timeframe: formData.timeframe,
      from_date: fromDateTime,
      to_date: toDateTime,
    });

    post(`/api/dashboard/prices/fetch-historical?${params.toString()}`, {}, {
      showLoading: false,
      requiresAuthorization: true
    }).subscribe({
      next: (response: any) => {
        console.log('Historical data fetched successfully:', response);
        setLoading(false);
        onSuccess();
      },
      error: (err: any) => {
        console.error('Failed to fetch historical data:', err);
        alert(err?.message || 'Failed to fetch historical data');
        setLoading(false);
      },
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="field">
        <label className="label">Symbol</label>
        <div className="control">
          <input
            className="input"
            type="text"
            value={formData.symbol}
            onChange={(e) => setFormData({ ...formData, symbol: e.target.value })}
            required
            readOnly
          />
        </div>
      </div>

      <div className="field">
        <label className="label">Exchange</label>
        <div className="control">
          <div className="select is-fullwidth">
            <select
              value={formData.exchange}
              onChange={(e) => setFormData({ ...formData, exchange: e.target.value })}
            >
              <option value="NSE">NSE</option>
              <option value="BSE">BSE</option>
            </select>
          </div>
        </div>
      </div>

      <div className="field">
        <label className="label">Timeframe</label>
        <div className="control">
          <div className="select is-fullwidth">
            <select
              value={formData.timeframe}
              onChange={(e) => setFormData({ ...formData, timeframe: e.target.value })}
            >
              <option value="1m">1 Minute</option>
              <option value="2m">2 Minutes</option>
              <option value="5m">5 Minutes</option>
              <option value="15m">15 Minutes</option>
              <option value="30m">30 Minutes</option>
              <option value="1h">1 Hour</option>
              <option value="1d">1 Day</option>
            </select>
          </div>
        </div>
      </div>

      <div className="field">
        <label className="label">From Date</label>
        <div className="control">
          <input
            className="input"
            type="date"
            value={formData.from_date}
            onChange={(e) => setFormData({ ...formData, from_date: e.target.value })}
            required
          />
        </div>
      </div>

      <div className="field">
        <label className="label">To Date</label>
        <div className="control">
          <input
            className="input"
            type="date"
            value={formData.to_date}
            onChange={(e) => setFormData({ ...formData, to_date: e.target.value })}
            required
          />
        </div>
      </div>

      <div className="field is-grouped">
        <div className="control">
          <button
            type="submit"
            className={`button is-primary ${loading ? 'is-loading' : ''}`}
            disabled={loading}
          >
            Fetch Data
          </button>
        </div>
        <div className="control">
          <button
            type="button"
            className="button is-light"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
};

export default IndexDashboard;
